/**
 * Concurrency-limited thumbnail loader with in-flight dedupe and 429 retry.
 *
 * Opening a flow with many assets otherwise fires 30+ simultaneous
 * `/api/media/thumbnail` requests and trips the server rate limiter.
 * This queue caps concurrent fetches so the burst is spread across seconds,
 * and retries transient 429s with exponential backoff + jitter.
 *
 * Consumers use the `useThumbnail` hook; direct callers can use `loadThumbnail`.
 */

const MAX_CONCURRENT = 6;
const MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 750;
const JITTER_MS = 500;

export type ThumbnailStatus = 'pending' | 'loaded' | 'error' | 'rate-limited';

type Waiter = () => void;
const waiting: Waiter[] = [];
let active = 0;

function acquire(): Promise<void> {
  if (active < MAX_CONCURRENT) {
    active++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    waiting.push(() => {
      active++;
      resolve();
    });
  });
}

function release(): void {
  active--;
  const next = waiting.shift();
  if (next) next();
}

/** Subscribers share one blob URL per source URL until it's revoked. */
const cache = new Map<string, Promise<string>>();

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchThumbnail(url: string): Promise<{ ok: true; blobUrl: string } | { ok: false; kind: 'rate-limited' | 'error'; status?: number }> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    let res: Response;
    try {
      res = await fetch(url, { credentials: 'include' });
    } catch {
      // Network-level failure — one retry only.
      if (attempt === 0) {
        await sleep(BASE_BACKOFF_MS);
        continue;
      }
      return { ok: false, kind: 'error' };
    }

    if (res.ok) {
      const blob = await res.blob();
      return { ok: true, blobUrl: URL.createObjectURL(blob) };
    }

    if (res.status === 429 && attempt < MAX_RETRIES) {
      const backoff = BASE_BACKOFF_MS * Math.pow(2, attempt) + Math.random() * JITTER_MS;
      await sleep(backoff);
      continue;
    }

    if (res.status === 429) {
      return { ok: false, kind: 'rate-limited', status: 429 };
    }

    return { ok: false, kind: 'error', status: res.status };
  }
  return { ok: false, kind: 'rate-limited', status: 429 };
}

export interface LoadThumbnailResult {
  status: Exclude<ThumbnailStatus, 'pending'>;
  blobUrl?: string;
  httpStatus?: number;
}

/**
 * Load (or reuse) a thumbnail blob URL for `url`, respecting the concurrency cap.
 * Identical concurrent calls for the same URL share one fetch and one blob URL.
 */
export async function loadThumbnail(url: string): Promise<LoadThumbnailResult> {
  const existing = cache.get(url);
  if (existing) {
    const blobUrl = await existing;
    return { status: 'loaded', blobUrl };
  }

  const promise = (async (): Promise<string> => {
    await acquire();
    try {
      const result = await fetchThumbnail(url);
      if (result.ok) return result.blobUrl;
      // Attach failure kind to the rejection so callers can distinguish.
      const err = new Error(result.kind);
      (err as { kind?: string; httpStatus?: number }).kind = result.kind;
      (err as { kind?: string; httpStatus?: number }).httpStatus = result.status;
      throw err;
    } finally {
      release();
    }
  })();

  cache.set(url, promise);

  try {
    const blobUrl = await promise;
    return { status: 'loaded', blobUrl };
  } catch (e) {
    // Remove failed entries so a manual retry won't be stuck on the rejection.
    cache.delete(url);
    const err = e as { kind?: string; httpStatus?: number };
    if (err.kind === 'rate-limited') {
      return { status: 'rate-limited', httpStatus: err.httpStatus };
    }
    return { status: 'error', httpStatus: err.httpStatus };
  }
}

/**
 * Manually evict a URL from the cache and revoke its blob URL.
 * Call when retrying a failed load, or when the URL will never be shown again.
 */
export function evictThumbnail(url: string): void {
  const promise = cache.get(url);
  cache.delete(url);
  if (!promise) return;
  promise.then(URL.revokeObjectURL).catch(() => { /* already failed, nothing to revoke */ });
}
